import { OrderCancellationCode } from "@loot-os/core/db/schema";

import type { OrderLineStatus, OrderStatus, OrderType } from "./types";

/**
 * Unified order state machine.
 *
 * Physical (TCG / MARKETPLACE) happy path — ported from boxed tcgOrder:
 *   PENDING -> PROCESSING -> PRESHIPPED -> SHIPPED
 *   (any non-terminal) -> CANCELLED
 * preship requires PROCESSING; ship requires PROCESSING or PRESHIPPED
 * (see boxed preshipTcgOrder / shipTcgOrder).
 *
 * Review-gated / digital (WITHDRAW_* / SELL_FOR_CRYPTO), ported from boxed
 * WithdrawOrderStatus, adds an approval gate and instant success/fail terminals:
 *   UNAPPROVED -> APPROVED | HOLD | CANCELLED
 *   HOLD       -> APPROVED | CANCELLED
 *   APPROVED   -> PROCESSING | SUCCESS | FAIL | CANCELLED
 *
 * A buy-on-demand MARKETPLACE order walks the physical path above, driven by
 * whoever buys on that vendor's site rather than by a warehouse: the Fanatics
 * bot on a BUY_BOT order, a human fulfiller on a MANUAL_BUY one. Each status
 * names a checkpoint on the vendor's own site:
 *   PENDING    awaiting whoever buys it
 *   PROCESSING the buy is placed on the vendor site
 *   PRESHIPPED the vendor authenticated / verified the item
 *   SHIPPED    the vendor shipped it, trackingNumber set. End of the happy path -
 *              we do not track delivery - but an operator may still cancel from
 *              here (boxed adminCancelTcgOrder allows it), so it is not a dead end.
 *   CANCELLED  the fulfiller could not complete the buy; the cause is in
 *              cancellationCode and their own words in cancellationReason
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  UNAPPROVED: ["APPROVED", "HOLD", "CANCELLED"],
  HOLD: ["APPROVED", "CANCELLED"],
  APPROVED: ["PROCESSING", "SUCCESS", "FAIL", "CANCELLED"],
  PENDING: ["PROCESSING", "CANCELLED"],
  PROCESSING: ["PRESHIPPED", "SHIPPED", "CANCELLED"],
  PRESHIPPED: ["SHIPPED", "CANCELLED"],
  SHIPPED: ["CANCELLED"],
  SUCCESS: [],
  FAIL: [],
  CANCELLED: [],
};

/**
 * Targets an order type never takes, though the state machine has the edge for other types.
 * A physical withdrawal ships or is cancelled, as a boxed TcgOrder does, which has no FAIL:
 * orders.cancel settles the shopper and refunds the shipping fee, and a FAIL does neither.
 * The digital types (WITHDRAW_STEAM, SELL_FOR_CRYPTO) keep APPROVED -> FAIL.
 */
const TARGETS_REFUSED_BY_TYPE: Partial<Record<OrderType, readonly OrderStatus[]>> = {
  WITHDRAW_ASSET: ["FAIL"],
};

export function isTargetRefusedForType(type: OrderType, to: OrderStatus): boolean {
  return TARGETS_REFUSED_BY_TYPE[type]?.includes(to) ?? false;
}

export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = [
  "SHIPPED",
  "SUCCESS",
  "FAIL",
  "CANCELLED",
];

export function isTerminalOrderStatus(status: OrderStatus): boolean {
  return TERMINAL_ORDER_STATUSES.includes(status);
}

/**
 * Statuses no cancel may act from, whoever asks: the order is already cancelled, or it reached a
 * digital terminal. SUCCESS / FAIL belong to the review-gated flows and have no boxed
 * `TcgOrderStatus` counterpart, so no parity argument reaches them - a completed crypto send is
 * not cancellable.
 */
export const UNCANCELLABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  "CANCELLED",
  "SUCCESS",
  "FAIL",
];

/**
 * Statuses an end user may cancel from, matching boxed `userCancelOrder`: once the order is being
 * prepared it is the operator's to cancel, not theirs.
 */
export const USER_CANCELLABLE_ORDER_STATUSES: readonly OrderStatus[] = ["PENDING", "PROCESSING"];

/**
 * Whether a cancel carrying `code` may act on an order in `status`.
 *
 * The gate is the cancellation code because that is what carries the initiator. `TENANT_CANCELLED`
 * is an end user asking, and is held to boxed's own user rule; every other code - an operator
 * (`TENANT_ADMIN_CANCELLED`) or a fulfiller cause (`OUT_OF_STOCK`, `PRICE_EXCEEDED`) - may act on
 * anything not already finished, SHIPPED included. A missing code is treated as an operator: it is
 * what every caller sent before the code existed.
 */
export function canCancelOrderFrom(status: OrderStatus, code?: OrderCancellationCode): boolean {
  if (UNCANCELLABLE_ORDER_STATUSES.includes(status)) return false;
  if (code === OrderCancellationCode.TENANT_CANCELLED) {
    return USER_CANCELLABLE_ORDER_STATUSES.includes(status);
  }
  return true;
}

export function canTransitionOrder(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from]?.includes(to) ?? false;
}

export function assertOrderTransition(from: OrderStatus, to: OrderStatus): void {
  if (!canTransitionOrder(from, to)) {
    throw new Error(`Invalid order transition: ${from} -> ${to}`);
  }
}

/**
 * Order line state machine (boxed TcgOrderItemStatus):
 *   PENDING -> PACKED | CANCELLED
 *   PACKED  -> CANCELLED   (a packed card can still be pulled/cancelled)
 */
export const ORDER_LINE_TRANSITIONS: Record<OrderLineStatus, readonly OrderLineStatus[]> = {
  PENDING: ["PACKED", "CANCELLED"],
  PACKED: ["CANCELLED"],
  CANCELLED: [],
};

export function canTransitionOrderLine(from: OrderLineStatus, to: OrderLineStatus): boolean {
  return ORDER_LINE_TRANSITIONS[from]?.includes(to) ?? false;
}

export function assertOrderLineTransition(from: OrderLineStatus, to: OrderLineStatus): void {
  if (!canTransitionOrderLine(from, to)) {
    throw new Error(`Invalid order line transition: ${from} -> ${to}`);
  }
}

/** Ship is allowed from PROCESSING or PRESHIPPED (boxed shipTcgOrder guard). */
export function canShip(status: OrderStatus): boolean {
  return status === "PROCESSING" || status === "PRESHIPPED";
}

/** Preship requires PROCESSING (boxed preshipTcgOrder guard). */
export function canPreship(status: OrderStatus): boolean {
  return status === "PROCESSING";
}
